import { osnovaApi } from '../base';
import type {
  GroupRate,
  GroupRateEffectiveList,
  GroupRateListFilter,
  GroupRateObject,
  GroupRateSuggestArgs,
  GroupRateSuggestion,
  NewGroupRate,
} from '../types';

/**
 * Средние ставки маш.-часа групп механизаторов по объектам
 * (`/api/v1/agents/econ/group-rates`, ТЗ AI-Economist-2026 ред. 2.1, п. 6.3.2–6.3.3,
 * 7.3). Чтение — право чтения Экономиста, запись — право записи.
 *
 * Теги: `{EconGroupRate, 'LIST'}` — ставки на дату и история (новая ставка
 * закрывает прежнюю, поэтому сбрасываются все), `{EconGroupRate, 'OBJECTS'}` —
 * объекты для выбора. Ставки на дату перечисляют группы — подписаны и на
 * список групп; подсказка зависит от состава группы и ПРЦ её моделей.
 */

// `as const` — литералы типов вместо string: иначе RTK не узнает теги из tagTypes.
const RATES = { type: 'EconGroupRate', id: 'LIST' } as const;
const OBJECTS = { type: 'EconGroupRate', id: 'OBJECTS' } as const;
const GROUPS = { type: 'FleetGroup', id: 'LIST' } as const;
const FLEET_LIST = { type: 'Fleet', id: 'LIST' } as const;

export const econGroupRatesApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    /** Объекты для выбора — единый реестр объектов строительства. */
    listGroupRateObjects: b.query<{ items: GroupRateObject[] }, void>({
      query: () => '/agents/econ/group-rates/objects',
      providesTags: [OBJECTS],
    }),

    /**
     * Ставки всех действующих групп на дату. С `project_id` — объекта, по
     * умолчанию или нет; без него — только ставки по умолчанию для холдинга.
     */
    getEffectiveGroupRates: b.query<GroupRateEffectiveList, { project_id?: string; on: string }>({
      query: (params) => ({ url: '/agents/econ/group-rates/effective', params }),
      providesTags: [RATES, GROUPS],
    }),

    /** Ставки объекта или по умолчанию: действующие на дату, текущие или с историей. */
    listGroupRates: b.query<{ items: GroupRate[] }, GroupRateListFilter>({
      query: (params) => ({ url: '/agents/econ/group-rates', params }),
      providesTags: [RATES],
    }),

    /** Подсказка: средние утверждённых ПРЦ моделей группы на дату ставки. */
    suggestGroupRate: b.query<GroupRateSuggestion, GroupRateSuggestArgs>({
      query: ({ group_id, date, basis, model_ids, project_id }) => ({
        url: '/agents/econ/group-rates/suggest',
        params: {
          group_id,
          date,
          basis,
          ...(model_ids ? { model_ids: model_ids.join(',') } : {}),
          ...(project_id ? { project_id } : {}),
        },
      }),
      providesTags: (_r, _e, { group_id }) => [{ type: 'FleetGroup', id: group_id }, FLEET_LIST],
    }),

    /**
     * Новая ставка: прежняя того же объекта и группы закрывается днём раньше.
     * 409 с текстом — дата не позже действующей ставки (задним числом ставку не
     * меняют) или ставку только что ввёл кто-то другой.
     */
    createGroupRate: b.mutation<{ rate: GroupRate }, NewGroupRate>({
      query: (body) => ({ url: '/agents/econ/group-rates', method: 'POST', body }),
      invalidatesTags: [RATES],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListGroupRateObjectsQuery,
  useGetEffectiveGroupRatesQuery,
  useListGroupRatesQuery,
  useSuggestGroupRateQuery,
  useCreateGroupRateMutation,
} = econGroupRatesApi;
